import { useLayoutEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { setTokenProvider } from './api/client';
import { Layout } from './components/Layout';

// Pages
import LoginPage from './pages/Login';
import FleetPage from './pages/Fleet';
import DashboardsPage from './pages/Dashboards';
import DeviceDetailPage from './pages/DeviceDetail';
import BillingPage from './pages/Billing';
import RemotePage from './pages/Remote';
import UsersPage from './pages/Users';
import ModelsPage from './pages/Models';
import SkusPage from './pages/Skus';
import SettingsPage from './pages/Settings';
import IntegratorsPage from './pages/Integrators';
import MyClientsPage from './pages/MyClients';
import CommissioningPage from './pages/Commissioning';
import OTAPage from './pages/OTA';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-0">
        <div className="text-center">
          <div className="w-12 h-12 bg-kinetica-600 rounded-xl flex items-center justify-center text-white font-bold text-xl mx-auto mb-4 animate-pulse shadow-lg shadow-kinetica-600/20">
            K
          </div>
          <p className="text-sm font-medium text-gray-400">Loading Kinetica Portal</p>
          <p className="text-xs text-gray-600 mt-1">Verifying your session...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

export default function App() {
  const { getAccessToken, isAuthenticated } = useAuth();

  // Wire up API client token provider using useLayoutEffect so it runs before
  // child useEffect hooks (react-query fires queries in useEffect). All layout
  // effects across the entire subtree complete before any regular effects fire,
  // so the token provider is guaranteed to be set before the first query runs.
  useLayoutEffect(() => {
    if (isAuthenticated) {
      setTokenProvider(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/fleet" element={<FleetPage />} />
        <Route path="/fleet/:serial" element={<DeviceDetailPage />} />
        <Route path="/dashboards" element={<DashboardsPage />} />
        <Route path="/dashboards/:id" element={<DashboardsPage />} />
        <Route path="/commissioning" element={<CommissioningPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/remote" element={<RemotePage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/models" element={<ModelsPage />} />
        <Route path="/skus" element={<SkusPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/integrators" element={<IntegratorsPage />} />
        <Route path="/clients" element={<MyClientsPage />} />
        <Route path="/ota" element={<OTAPage />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/fleet" replace />} />
        <Route path="*" element={<Navigate to="/fleet" replace />} />
      </Route>
    </Routes>
  );
}
